import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import DefendantList from './pages/DefendantList';
import DefendantProfile from './pages/DefendantProfile';
import BondList from './pages/BondList';
import BondDetail from './pages/BondDetail';
import NewBond from './pages/NewBond';
import CourtDateList from './pages/CourtDateList';
import CheckinList from './pages/CheckinList';
import PaymentList from './pages/PaymentList';
import Powers from './pages/Powers';
import StaffManagement from './pages/StaffManagement';
import ChargesManagement from './pages/ChargesManagement';
import UserManagement from './pages/UserManagement';
import AddressBook from './pages/AddressBook';
import Courts from './pages/Courts';
import CourtDetail from './pages/CourtDetail';
import CountyAttorneys from './pages/CountyAttorneys';
import PublicDefenders from './pages/PublicDefenders';
import ProbationParoleOffices from './pages/ProbationParoleOffices';
import ProbationParoleOfficeDetail from './pages/ProbationParoleOfficeDetail';
import SheriffOffices from './pages/SheriffOffices';
import SheriffOfficeDetail from './pages/SheriffOfficeDetail';
import PretrialServices from './pages/PretrialServices';
import PretrialServiceDetail from './pages/PretrialServiceDetail';
import BailAgencies from './pages/BailAgencies';
import BailAgencyDetail from './pages/BailAgencyDetail';
import DispatchCenters from './pages/DispatchCenters';
import DispatchCenterDetail from './pages/DispatchCenterDetail';
import Jails from './pages/Jails';
import JailDetail from './pages/JailDetail';
import CountyContacts from './pages/CountyContacts';
import UserProfile from './pages/UserProfile';

// Components
import Layout from './components/Layout';

// API configuration
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);

        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }

    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    setUser(userData);
    setIsAuthenticated(true);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const handleLogout = () => {
    setUser(null);
    setIsAuthenticated(false);
    delete axios.defaults.headers.common['Authorization'];
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Login onLogin={handleLogin} />
            )
          }
        />

        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />

        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Layout user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="profile" element={<UserProfile />} />
          <Route path="defendants" element={<DefendantList />} />
          <Route path="defendants/:id" element={<DefendantProfile />} />
          <Route path="bonds" element={<BondList />} />
          <Route path="bonds/:id" element={<BondDetail />} />
          <Route path="bonds/new" element={<NewBond />} />
          <Route path="court-dates" element={<CourtDateList />} />
          <Route path="checkins" element={<CheckinList />} />
          <Route path="payments" element={<PaymentList />} />
          <Route path="address-book" element={<AddressBook />} />
          <Route path="address-book/county/:county" element={<CountyContacts />} />
          <Route path="address-book/county-attorneys" element={<CountyAttorneys />} />
          <Route path="address-book/public-defenders" element={<PublicDefenders />} />
          <Route path="address-book/courts/:id" element={<CourtDetail />} />
          <Route path="address-book/courts" element={<Courts />} />
          <Route path="address-book/probation-parole/:id" element={<ProbationParoleOfficeDetail />} />
          <Route path="address-book/probation-parole" element={<ProbationParoleOffices />} />
          <Route path="address-book/sheriff-offices/:id" element={<SheriffOfficeDetail />} />
          <Route path="address-book/sheriff-offices" element={<SheriffOffices />} />
          <Route path="address-book/pretrial-services/:id" element={<PretrialServiceDetail />} />
          <Route path="address-book/pretrial-services" element={<PretrialServices />} />
          <Route path="address-book/bail-agencies/:id" element={<BailAgencyDetail />} />
          <Route path="address-book/bail-agencies" element={<BailAgencies />} />
          <Route path="address-book/dispatch-centers/:id" element={<DispatchCenterDetail />} />
          <Route path="address-book/dispatch-centers" element={<DispatchCenters />} />
          <Route path="address-book/jails/:id" element={<JailDetail />} />
          <Route path="address-book/jails" element={<Jails />} />
          <Route path="admin/powers" element={<Powers />} />
          <Route path="admin/staff" element={<StaffManagement />} />
          <Route path="admin/charges" element={<ChargesManagement />} />
          <Route path="admin/users" element={<UserManagement />} />
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
